import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, Clock, Star, Sparkles, Zap, Shield, Phone, ArrowRight } from 'lucide-react';
import AppointmentForm from '@/components/forms/AppointmentForm';
import { ServiceSchema, BreadcrumbSchema } from '@/components/seo/SchemaMarkup';

const FirstFitVeneers = () => {
  const benefits = [
    {
      icon: <Clock className="w-8 h-8" />,
      title: 'Same-Day Smile',
      description: 'Get your brand-new smile in as little as 2 hours - no multiple appointments needed'
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Digital Precision',
      description: 'Advanced digital technology ensures perfect fit and natural-looking results'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Minimally Invasive',
      description: 'Preserves more of your natural tooth structure compared to traditional veneers'
    },
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: 'Instant Transformation',
      description: 'See your new smile immediately - no temporary veneers or waiting weeks'
    }
  ];

  const treatedConditions = [
    'Chipped or cracked teeth',
    'Discolored or stained teeth',
    'Gaps between teeth',
    'Misshapen teeth',
    'Minor misalignment',
    'Worn down teeth',
    'Uneven smile'
  ];

  const doctors = [
    { name: 'Dr. Olena Razo', title: 'D.D.S.', image: '/images/team/dr-razo.jpg' },
    { name: 'Dr. Omar Hiwiadi', title: 'D.M.D.', image: '/images/team/dr-omar.jpg' },
    { name: 'Dr. Joshua Pham', title: 'D.D.S.', image: '/images/team/dr-pham.jpg' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>First Fit Veneers Fort Worth TX | Same-Day Smile Transformation | Fort Worth Dental</title>
        <meta name="description" content="First Fit Veneers - the most innovative dental procedure available. Get a brand-new smile in just 2 hours! Digital precision, minimally invasive. Call (817) 985-3337." />
        <meta name="keywords" content="first fit veneers fort worth, same day veneers, instant veneers, digital veneers, cosmetic dentistry fort worth tx, smile makeover, porcelain veneers" />
        <link rel="canonical" href="https://fortworthdental.com/first-fit-veneers" />
      </Helmet>
      
      <ServiceSchema 
        serviceName="First Fit Veneers"
        description="Revolutionary same-day veneers using digital technology. Transform your smile in just 2 hours with First Fit Veneers at Fort Worth Dental."
        url="https://fortworthdental.com/first-fit-veneers"
      />
      <BreadcrumbSchema items={[
        { name: "Home", url: "https://fortworthdental.com/" },
        { name: "First Fit Veneers", url: "https://fortworthdental.com/first-fit-veneers" }
      ]} />

      {/* Hero Section — premium cosmetic redesign */}
      <style>{`
        .ffv-hero{background:#0a0f1c;}
        .ffv-glow{position:absolute;border-radius:9999px;filter:blur(90px);pointer-events:none;}
        .ffv-glow-teal{width:480px;height:480px;background:#0d9488;top:-140px;left:-120px;opacity:.45;}
        .ffv-glow-gold{width:440px;height:440px;background:#b8860b;bottom:-160px;right:-90px;opacity:.38;}
        .ffv-grain{position:absolute;inset:0;background-image:radial-gradient(rgba(255,255,255,.06) 1px,transparent 1px);background-size:22px 22px;opacity:.5;pointer-events:none;}
        .ffv-fade{opacity:0;transform:translateY(18px);animation:ffvUp .7s cubic-bezier(.22,1,.36,1) forwards;}
        @keyframes ffvUp{to{opacity:1;transform:none;}}
        .ffv-shine{background:linear-gradient(110deg,#fde68a 20%,#ffffff 42%,#d4af37 60%,#fde68a 82%);background-size:200% auto;-webkit-background-clip:text;background-clip:text;color:transparent;animation:ffvShimmer 3.5s linear infinite;}
        @keyframes ffvShimmer{to{background-position:200% center;}}
        .ffv-divider{height:1px;background:linear-gradient(90deg,transparent,rgba(212,175,55,.6),transparent);}
        .ffv-cta{box-shadow:0 10px 30px -8px rgba(251,191,36,.5);}
      `}</style>
      <section className="ffv-hero relative overflow-hidden text-white">
        <div className="ffv-glow ffv-glow-teal" />
        <div className="ffv-glow ffv-glow-gold" />
        <div className="ffv-grain" />
        <div className="relative container mx-auto px-4 py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: copy */}
            <div className="ffv-fade">
              <div className="inline-flex items-center gap-2 rounded-full border border-amber-300/30 bg-amber-300/10 px-4 py-2 mb-6 backdrop-blur-sm">
                <Sparkles className="w-4 h-4 text-amber-300" />
                <span className="text-sm font-medium tracking-wide text-amber-100">Fort Worth's most advanced smile transformation</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-5">
                A Flawless <span className="ffv-shine">Smile</span>
                <br />in as little as <span className="text-amber-300">2 hours</span>
              </h1>
              <p className="text-lg text-slate-300 mb-8 max-w-xl leading-relaxed">
                First Fit Veneers use digital precision to give you a stunning,
                natural-looking smile in a single visit — no weeks of waiting,
                no temporaries, no compromise.
              </p>
              <div className="flex flex-wrap gap-x-6 gap-y-2 mb-9 text-sm text-slate-300">
                <span className="inline-flex items-center gap-2"><Star className="w-4 h-4 text-amber-300 fill-amber-300" />1,100+ 5-star reviews</span>
                <span className="inline-flex items-center gap-2"><Clock className="w-4 h-4 text-teal-300" />Same-day results</span>
                <span className="inline-flex items-center gap-2"><Shield className="w-4 h-4 text-teal-300" />Minimally invasive</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact-us"
                  data-testid="hero-cta"
                  className="ffv-cta group inline-flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold px-8 py-4 rounded-full text-lg transition-colors"
                >
                  Schedule FREE Consultation
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                </Link>
                <a
                  href="tel:8179853337"
                  className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-amber-300 hover:text-amber-200 text-white px-8 py-4 rounded-full font-bold text-lg transition-colors backdrop-blur-sm"
                >
                  <Phone className="w-5 h-5" />
                  (817) 985-3337
                </a>
              </div>
            </div>

            {/* Right: real smile photo with floating badges (human imagery) */}
            <div className="ffv-fade relative" style={{ animationDelay: '.18s' }}>
              <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
                <img
                  src="/images/services/whitening-new-1.jpg"
                  alt="Happy patient with a radiant, natural-looking smile after First Fit Veneers in Fort Worth"
                  className="w-full h-[360px] lg:h-[460px] object-cover object-top"
                  loading="eager"
                  width={800}
                  height={1200}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/55 via-transparent to-transparent" />
              </div>
              {/* floating 2-hour gold badge */}
              <div className="absolute -top-5 -left-5 bg-gradient-to-br from-amber-300 to-amber-500 text-slate-900 rounded-2xl px-5 py-3 shadow-xl">
                <div className="text-2xl font-black leading-none">2 hrs</div>
                <div className="text-[11px] font-semibold tracking-wide uppercase">to a new smile</div>
              </div>
              {/* glass trust chip */}
              <div className="absolute -bottom-5 right-6 bg-white/10 backdrop-blur-xl border border-white/15 rounded-2xl px-5 py-3 flex items-center gap-2">
                <Star className="w-6 h-6 text-amber-300 fill-amber-300" />
                <div>
                  <div className="text-white font-bold text-sm leading-tight">1,100+ 5-star reviews</div>
                  <div className="text-slate-300 text-[11px]">Best of Fort Worth 2025</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Are First Fit Veneers */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-6 text-center">
              What Are First Fit Veneers?
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              First Fit Veneers are recognized as <strong>the most innovative dental procedure available today</strong>. 
              This revolutionary digital technology enables patients to receive a brand-new smile in as short as two hours - 
              a process that traditionally takes multiple appointments over several weeks.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Using advanced digital scanning and precision manufacturing, First Fit Veneers are custom-designed 
              to perfectly match your teeth and facial features. The result is a natural, beautiful smile that 
              looks and feels like your own teeth.
            </p>
            <div className="bg-purple-50 border-l-4 border-purple-500 p-6 rounded-r-lg">
              <p className="text-purple-800 font-semibold text-lg">
                "First Fit Veneers represent the cutting edge of cosmetic dentistry. We're proud to offer this 
                transformative technology to our patients in Fort Worth."
              </p>
              <p className="text-purple-600 mt-2">— Dr. Arthur Mund, D.D.S.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-800 mb-12 text-center">
            Why Choose First Fit Veneers?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg text-center hover:shadow-xl transition-shadow">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 text-purple-600 rounded-full mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Process */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
              The First Fit Veneers Process
            </h2>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">1</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Digital Scan & Design</h3>
                  <p className="text-gray-600">We take precise digital impressions of your teeth and design your perfect smile using advanced software. You'll see a preview before we begin.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">2</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Precision Manufacturing</h3>
                  <p className="text-gray-600">Your custom veneers are created on-site using state-of-the-art digital manufacturing technology, ensuring a perfect fit.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">3</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Same-Day Placement</h3>
                  <p className="text-gray-600">Your veneers are bonded to your teeth with precision. Walk out with your brand-new smile the same day!</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Can Be Treated */}
      <section className="py-16 bg-gradient-to-br from-teal-50 to-purple-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
              What First Fit Veneers Can Fix
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {treatedConditions.map((condition, index) => (
                <div key={index} className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">{condition}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our Certified Doctors */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
            Our First Fit Veneers Certified Doctors
          </h2>
          <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto">
            All four of our doctors are certified in First Fit Veneers technology, ensuring you receive expert care regardless of which dentist you see.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {doctors.map((doctor, index) => (
              <div key={index} className="text-center">
                <div className="w-24 h-24 mx-auto mb-3 rounded-full overflow-hidden border-4 border-purple-200">
                  <img 
                    src={doctor.image} 
                    alt={`${doctor.name} - First Fit Veneers certified dentist Fort Worth`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <h3 className="font-bold text-gray-800">{doctor.name}</h3>
                <p className="text-purple-600 text-sm">{doctor.title}</p>
                <p className="text-xs text-gray-500 mt-1">First Fit Certified</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-purple-700 to-teal-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Ready for Your Same-Day Smile Transformation?
          </h2>
          <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
            Schedule your FREE First Fit Veneers consultation today and discover how you can have a brand-new smile in just 2 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact-us" 
              className="bg-white text-purple-700 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors"
            >
              Book FREE Consultation
            </Link>
            <a 
              href="tel:8179853337" 
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white/10 transition-colors"
            >
              Call (817) 985-3337
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section with Schema */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Frequently Asked Questions About First Fit Veneers</h2>
          <div className="space-y-6">
            {[
              {
                q: "How long does the First Fit Veneers procedure take?",
                a: "Most patients receive their brand-new smile in as little as 2 hours. Unlike traditional veneers that require multiple visits over several weeks, First Fit Veneers use digital technology to design, create, and place your veneers in a single appointment."
              },
              {
                q: "Are First Fit Veneers painful?",
                a: "The procedure is minimally invasive with little to no discomfort. Many patients report that the process is more comfortable than traditional veneer placement because there is minimal tooth preparation needed."
              },
              {
                q: "How much do First Fit Veneers cost?",
                a: "The cost varies depending on the number of veneers needed and your specific treatment plan. We offer a FREE consultation where we'll discuss your goals and provide a personalized treatment estimate. Financing options are available."
              },
              {
                q: "How long do First Fit Veneers last?",
                a: "With proper care and regular dental visits, First Fit Veneers can last many years. They are made from high-quality materials and are designed to be durable and stain-resistant."
              },
              {
                q: "Am I a good candidate for First Fit Veneers?",
                a: "Most people who want to improve the appearance of their smile are good candidates. First Fit Veneers can correct chips, gaps, discoloration, and minor misalignment. Schedule a free consultation with our certified doctors to find out if First Fit Veneers are right for you."
              },
              {
                q: "Which doctors at Fort Worth Dental are First Fit Veneers certified?",
                a: "Our First Fit Veneers certified doctors include Dr. Olena Razo, Dr. Omar Hiwiadi, and Dr. Joshua Pham. You can trust that you're in expert hands."
              },
              {
                q: "What's the difference between First Fit Veneers and traditional veneers?",
                a: "Traditional veneers require multiple appointments, temporary veneers, and weeks of waiting for a dental lab. First Fit Veneers use advanced digital technology to complete the entire process in one visit, with greater precision and less tooth preparation."
              }
            ].map((faq, i) => (
              <details key={i} className="group border border-gray-200 rounded-lg" data-testid={`faq-item-${i}`}>
                <summary className="flex justify-between items-center cursor-pointer p-5 font-semibold text-gray-800 hover:text-teal-600">
                  {faq.q}
                  <span className="ml-2 text-teal-500 group-open:rotate-180 transition-transform">&#9660;</span>
                </summary>
                <div className="px-5 pb-5 text-gray-700 leading-relaxed">{faq.a}</div>
              </details>
            ))}
          </div>
        </div>
        {/* FAQ Schema */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            { "@type": "Question", "name": "How long does the First Fit Veneers procedure take?", "acceptedAnswer": { "@type": "Answer", "text": "Most patients receive their brand-new smile in as little as 2 hours." }},
            { "@type": "Question", "name": "Are First Fit Veneers painful?", "acceptedAnswer": { "@type": "Answer", "text": "The procedure is minimally invasive with little to no discomfort." }},
            { "@type": "Question", "name": "How much do First Fit Veneers cost?", "acceptedAnswer": { "@type": "Answer", "text": "Cost varies depending on the number of veneers. We offer a FREE consultation with a personalized treatment estimate." }},
            { "@type": "Question", "name": "How long do First Fit Veneers last?", "acceptedAnswer": { "@type": "Answer", "text": "With proper care, First Fit Veneers can last many years." }},
            { "@type": "Question", "name": "Am I a good candidate for First Fit Veneers?", "acceptedAnswer": { "@type": "Answer", "text": "Most people who want to improve their smile are good candidates. Schedule a free consultation to find out." }},
            { "@type": "Question", "name": "Which doctors are First Fit Veneers certified?", "acceptedAnswer": { "@type": "Answer", "text": "Dr. Olena Razo, Dr. Omar Hiwiadi, and Dr. Joshua Pham are certified in First Fit Veneers at Fort Worth Dental." }},
            { "@type": "Question", "name": "What's the difference between First Fit Veneers and traditional veneers?", "acceptedAnswer": { "@type": "Answer", "text": "First Fit Veneers use digital technology to complete the process in one visit, with greater precision and less tooth preparation." }}
          ]
        })}} />
      </section>

      {/* Related Services */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Related Cosmetic Services</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/porcelain-veneers" className="bg-white p-4 rounded-lg shadow hover:shadow-md transition text-center">
              <h3 className="font-semibold text-teal-600 text-sm">Porcelain Veneers</h3>
            </Link>
            <Link to="/teeth-whitening" className="bg-white p-4 rounded-lg shadow hover:shadow-md transition text-center">
              <h3 className="font-semibold text-teal-600 text-sm">Teeth Whitening</h3>
            </Link>
            <Link to="/invisalign" className="bg-white p-4 rounded-lg shadow hover:shadow-md transition text-center">
              <h3 className="font-semibold text-teal-600 text-sm">Invisalign</h3>
            </Link>
            <Link to="/dental-implants" className="bg-white p-4 rounded-lg shadow hover:shadow-md transition text-center">
              <h3 className="font-semibold text-teal-600 text-sm">Dental Implants</h3>
            </Link>
          </div>
        </div>
      </section>

      {/* Appointment Form */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
              Schedule Your First Fit Veneers Consultation
            </h2>
            <p className="text-gray-600 text-center mb-8">
              Serving Fort Worth, Aledo, Benbrook, and surrounding communities. Experience the future of cosmetic dentistry.
            </p>
            <AppointmentForm source="first_fit_veneers" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default FirstFitVeneers;
